import { useMemo, useCallback } from 'react';
import { useApi } from '../../provider';
import { useQuery, useMutation, useInfiniteQuery } from '../../hooks';
import { queryCache } from '../../cache';
import { ProductService } from './service';
import {
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  ProductsQueryParams,
  ProductMasterItem,
  ProductUploadRequest,
  CommitChangesRequest,
  ProductCountResponse,
  ProductQueryResponse,
} from './types';

const CACHE_KEY = 'products';
const MASTER_CACHE_KEY = 'product-master';
const COUNT_CACHE_KEY = 'products-count';

// Regular product hooks
export function useProducts(params?: ProductsQueryParams) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  const cacheKey = useMemo(() => {
    if (!params) return CACHE_KEY;
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    // TODO: Enhance caching to be more targeted
    return `${CACHE_KEY}`;
  }, [params]);

  const fetcher = useCallback(() => service.getAll(params), [service, params]);

  return useQuery<ProductQueryResponse>(cacheKey, fetcher);
}

export function useProductsPaginated(params?: ProductsQueryParams) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const pageSize = params?.limit || 10;

  const cacheKey = useMemo(() => {
    if (!params) return CACHE_KEY;
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    // TODO: Enhance caching to be more targeted
    return `${CACHE_KEY}`;
  }, [params]);

  return useInfiniteQuery<ProductQueryResponse>(
    cacheKey,
    async (pageParam: unknown) => {
      const page = typeof pageParam === 'number' ? pageParam : 0;
      const queryParams = {
        ...params,
        offset: page * pageSize,
        limit: pageSize,
      };
      const response = await service.getAll(queryParams as ProductsQueryParams);
      return response;
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.products.length === pageSize
          ? allPages.length
          : undefined;
      },
    }
  );
}

export function useProduct(id: number) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const fetcher = useCallback(() => service.getById(id), [service, id]);

  return useQuery<Product>(`${CACHE_KEY}/${id}`, fetcher);
}

export function useProductCount(params?: { search?: string }) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  const cacheKey = useMemo(() => {
    if (!params?.search) return COUNT_CACHE_KEY;
    return `${COUNT_CACHE_KEY}?search=${encodeURIComponent(params.search)}`;
  }, [params?.search]);

  const fetcher = useCallback(
    () => service.getCount(params?.search),
    [service, params?.search]
  );

  const result = useQuery<ProductCountResponse>(cacheKey, fetcher);

  return useMemo(
    () => ({
      ...result,
      // Return the count directly for convenience
      count: result.status === 'success' ? result.data.total : undefined,
    }),
    [result]
  );
}

export function useCreateProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<Product, CreateProductRequest>(
    data => service.create(data),
    {
      onSuccess: product => {
        queryCache.invalidate(CACHE_KEY);
        queryCache.invalidatePrefix(COUNT_CACHE_KEY);
        if (product.product_id) {
          queryCache.invalidate(
            `${CACHE_KEY}?product_id=${product.product_id}`
          );
        }
      },
    }
  );
}

export function useUpdateProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<Product, UpdateProductRequest>(
    data => service.update(data),
    {
      onSuccess: _ => {
        // TODO: Enhance cache invalidation to be more targeted
        queryCache.invalidate(CACHE_KEY);
        // Invalidate count cache as counts might have changed
        queryCache.invalidatePrefix(COUNT_CACHE_KEY);
      },
    }
  );
}

export function useDeleteProduct() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<void, number>(id => service.delete(id), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
      queryCache.invalidatePrefix(COUNT_CACHE_KEY);
    },
  });
}

export function useDeleteAllProducts() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<void, void>(() => service.deleteAll(), {
    onSuccess: () => {
      queryCache.invalidatePrefix(CACHE_KEY);
      queryCache.invalidatePrefix(COUNT_CACHE_KEY);
    },
  });
}

// Product Master hooks
export function useProductMaster() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);
  const fetcher = useCallback(() => service.getMasterProducts(), [service]);

  return useQuery<ProductMasterItem[]>(MASTER_CACHE_KEY, fetcher);
}

export function useUpdateProductMaster(id: number) {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation<ProductMasterItem, UpdateProductRequest>(
    data => service.updateMasterProduct(id, data),
    {
      onSuccess: () => {
        queryCache.invalidate(MASTER_CACHE_KEY);
      },
    }
  );
}

// Convenience hook for Product Master operations
export function useProductMasterPage() {
  const productMaster = useProductMaster();
  const updateProductMaster = useUpdateProductMaster;

  return useMemo(
    () => ({
      // Query operations
      productMaster,

      // Mutation operations
      update: updateProductMaster,

      // Convenience getters for common states
      data: productMaster.status === 'success' ? productMaster.data : [],
      isLoading: productMaster.status === 'loading',
      isError: productMaster.status === 'error',
      error: productMaster.status === 'error' ? productMaster.error : null,

      // Actions
      refetch: productMaster.refetch,
      invalidate: productMaster.invalidate,
    }),
    [productMaster, updateProductMaster]
  );
}

// Product Upload hooks
export function useUploadProducts() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation(
    (data: ProductUploadRequest) => service.uploadProducts(data),
    {
      onSuccess: () => {
        queryCache.invalidatePrefix(CACHE_KEY);
        queryCache.invalidatePrefix(COUNT_CACHE_KEY);
      },
    }
  );
}

export function useCommitProductChanges() {
  const api = useApi();
  const service = useMemo(() => new ProductService(api), [api]);

  return useMutation(
    (data: CommitChangesRequest) => service.commitChanges(data),
    {
      onSuccess: () => {
        queryCache.invalidatePrefix(CACHE_KEY);
        queryCache.invalidatePrefix(COUNT_CACHE_KEY);
      },
    }
  );
}

// Convenience hook that combines all products operations
export function useProductsPage(params?: ProductsQueryParams) {
  const productResponse = useProducts(params);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();
  const deleteAllProducts = useDeleteAllProducts();
  const uploadProducts = useUploadProducts();
  const commitChanges = useCommitProductChanges();

  return useMemo(
    () => ({
      // Query operations
      products: productResponse,

      // Mutation operations
      create: createProduct,
      update: updateProduct,
      delete: deleteProduct,
      deleteAll: deleteAllProducts,
      upload: uploadProducts,
      commit: commitChanges,

      // Convenience getters for common states
      data:
        productResponse.status === 'success'
          ? (productResponse.data?.products ?? [])
          : [],
      total:
        productResponse.status === 'success'
          ? (productResponse.data?.total ?? 0)
          : 0,
      isLoading: productResponse.status === 'loading',
      isError: productResponse.status === 'error',
      error: productResponse.status === 'error' ? productResponse.error : null,

      // Actions
      refetch: productResponse.refetch,
      invalidate: productResponse.invalidate,
    }),
    [
      productResponse,
      createProduct,
      updateProduct,
      deleteProduct,
      deleteAllProducts,
      uploadProducts,
      commitChanges,
    ]
  );
}
